import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import {
  Box,
  Card,
  CardContent,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  TablePagination,
  Paper,
  Popover,
  TextField,
  Button,
  Skeleton,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import AssessmentOutlinedIcon from "@mui/icons-material/AssessmentOutlined";
import PeopleAltOutlinedIcon from "@mui/icons-material/PeopleAltOutlined";
import PeopleAltIcon from "@mui/icons-material/PeopleAlt";
import DescriptionIcon from "@mui/icons-material/Description";
import MenuBookIcon from "@mui/icons-material/MenuBook";
import CreditCardIcon from "@mui/icons-material/CreditCard";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import TrendingDownIcon from "@mui/icons-material/TrendingDown";
import QuizOutlinedIcon from "@mui/icons-material/QuizOutlined";
import PercentIcon from "@mui/icons-material/Percent";
import TaskAltIcon from "@mui/icons-material/TaskAlt";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import { toast, ToastContainer } from "react-toastify";
import { Line, Pie } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  ArcElement,
  Filler,
  Tooltip,
  Legend,
} from "chart.js";
import { getAdminDashboard, apiErrorMessage } from "../../api/assessmentDashboard";
import { listCourses } from "../../api/assessmentHierarchy";
import { cardSx, SURFACE } from "./shell/assessmentTheme";
import AssessmentEmptyState from "./shell/AssessmentEmptyState";
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  ArcElement,
  Filler,
  Tooltip,
  Legend,
);

const ACCENT = "#17479e";

// Matches the "glass card" look used elsewhere (MentorAnalytics / MentorPayments):
// translucent card + blur, colored top border, and a soft glow shadow tinted to the stat.
const cardStyle = {
  borderRadius: "14px",
  background: "#fff",
  border: `1px solid ${SURFACE.border}`,
};

const PIE_COLORS = [
  "#5c5ce6", "#3f8efc", "#37c26d", "#ff8a3d", "#ec4899",
  "#14b8a6", "#f59e0b", "#8b5cf6", "#ef4444", "#64748b",
];

const formatInt = (n) => Number(n ?? 0).toLocaleString("en-IN");
const formatPercent = (n) => `${Number(n ?? 0).toFixed(1)}%`;
const formatRupees = (n) =>
  `\u20B9${Number(n ?? 0).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;

// Growth is % change vs. the previous window of the same length. The backend
// reports the raw current value (not a %) when the previous window was empty,
// so a "new" jump is shown simply as a positive change.
const growthLabel = (g) => {
  const v = Number(g ?? 0);
  return `${v > 0 ? "+" : ""}${v.toFixed(1)}% vs previous period`;
};

// Stat card defs, styled like STAT_DEFS in MentorPayments.jsx (icon, iconBg gradient,
// borderColor + glowColor for the card shadow/top-border). `growth` is only present
// for the metrics that are window-based; all-time totals show a static caption.
const statCards = (d) => [
  {
    key: "students",
    label: "Total Students",
    value: formatInt(d.totalStudents),
    caption: "All time",
    icon: <PeopleAltIcon sx={{ fontSize: 18, color: "#7c4dff" }} />,
    iconBg: "linear-gradient(135deg, #f3f0ff, #d1c4e9)",
    borderColor: "#9575cd",
    glowColor: "rgba(149,117,205,0.2)",
  },
  {
    key: "tests",
    label: "Total Tests",
    value: formatInt(d.totalTests),
    caption: "All time",
    icon: <DescriptionIcon sx={{ fontSize: 18, color: "#42a5f5" }} />,
    iconBg: "linear-gradient(135deg, #f0f7ff, #bbdefb)",
    borderColor: "#64b5f6",
    glowColor: "rgba(100,181,246,0.2)",
  },
  {
    key: "questions",
    label: "Questions",
    value: formatInt(d.totalQuestions),
    caption: "All time",
    icon: <MenuBookIcon sx={{ fontSize: 18, color: "#66bb6a" }} />,
    iconBg: "linear-gradient(135deg, #f1f8f1, #dcedc8)",
    borderColor: "#81c784",
    glowColor: "rgba(129,199,132,0.2)",
  },
  {
    key: "revenue",
    label: "Revenue",
    value: formatRupees(d.totalRevenue),
    growth: d.totalRevenueGrowth,
    icon: <CreditCardIcon sx={{ fontSize: 18, color: "#ff9800" }} />,
    iconBg: "linear-gradient(135deg, #fff8f0, #ffe0b2)",
    borderColor: "#ffb74d",
    glowColor: "rgba(255,183,77,0.2)",
  },
  {
    key: "attempts",
    label: "Test Attempts",
    value: formatInt(d.totalAttempts),
    growth: d.totalAttemptsGrowth,
    icon: <QuizOutlinedIcon sx={{ fontSize: 18, color: "#ec407a" }} />,
    iconBg: "linear-gradient(135deg, #fff0f5, #f8bbd0)",
    borderColor: "#f06292",
    glowColor: "rgba(240,98,146,0.2)",
  },
  {
    key: "avgScore",
    label: "Avg Score",
    value: formatPercent(d.avgScorePercentage),
    growth: d.avgScorePercentageGrowth,
    icon: <PercentIcon sx={{ fontSize: 18, color: "#26a69a" }} />,
    iconBg: "linear-gradient(135deg, #e8f7f5, #b2dfdb)",
    borderColor: "#4db6ac",
    glowColor: "rgba(77,182,172,0.2)",
  },
  {
    key: "completion",
    label: "Completion Rate",
    value: formatPercent(d.completionRate),
    growth: d.completionRateGrowth,
    icon: <TaskAltIcon sx={{ fontSize: 18, color: "#5c6bc0" }} />,
    iconBg: "linear-gradient(135deg, #eef0fb, #c5cae9)",
    borderColor: "#7986cb",
    glowColor: "rgba(121,134,203,0.2)",
  },
];

// Trend buckets are daily, weekly or monthly depending on the window; the
// response only carries each bucket's start date, so infer the granularity
// from the spacing between the first two points.
const trendLabels = (trend) => {
  const dates = trend.map((t) => new Date(`${t.periodStart}T00:00:00`));
  const gapDays = dates.length > 1 ? (dates[1] - dates[0]) / 86400000 : 1;
  const monthly = gapDays >= 28;
  return dates.map((d) =>
    monthly
      ? d.toLocaleDateString("en-IN", { month: "short", year: "numeric" })
      : d.toLocaleDateString("en-IN", { day: "2-digit", month: "short" }),
  );
};

const lineData = (trend) => ({
  labels: trendLabels(trend),
  datasets: [
    {
      data: trend.map((t) => t.attemptCount),
      borderColor: "#5c5ce6",
      backgroundColor: "transparent",
      borderWidth: 2.5,
      pointBackgroundColor: "#5c5ce6",
      pointBorderColor: "#fff",
      pointBorderWidth: 2,
      pointRadius: 5,
      pointHoverRadius: 7,
      tension: 0.35,
    },
  ],
});

const lineOptions = (trend) => ({
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: { display: false },
    tooltip: {
      backgroundColor: "rgba(255,255,255,0.95)",
      titleColor: "#1a1a2e",
      bodyColor: "#555",
      borderColor: "#e0e0e0",
      borderWidth: 1,
      padding: 10,
      callbacks: {
        label: (ctx) => `${ctx.parsed.y} attempts`,
        afterLabel: (ctx) => `Avg score: ${formatPercent(trend[ctx.dataIndex]?.avgScorePercentage)}`,
      },
    },
  },
  scales: {
    x: {
      grid: { display: false },
      ticks: { color: "#9e9e9e", font: { size: 12 }, maxTicksLimit: 12 },
    },
    y: {
      beginAtZero: true,
      grid: { color: "#f0f0f0" },
      ticks: { color: "#9e9e9e", font: { size: 12 }, precision: 0 },
    },
  },
});

const pieColor = (i) => PIE_COLORS[i % PIE_COLORS.length];

const pieData = (studentsByCourse) => ({
  labels: studentsByCourse.map((s) => s.label),
  datasets: [
    {
      data: studentsByCourse.map((s) => s.studentCount),
      backgroundColor: studentsByCourse.map((_, i) => pieColor(i)),
      borderColor: "#fff",
      borderWidth: 3,
      hoverOffset: 8,
    },
  ],
});

const pieOptions = (studentsByCourse) => ({
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: { display: false },
    tooltip: {
      backgroundColor: "rgba(255,255,255,0.95)",
      titleColor: "#1a1a2e",
      bodyColor: "#555",
      borderColor: "#e0e0e0",
      borderWidth: 1,
      padding: 10,
      callbacks: {
        label: (ctx) => {
          const slice = studentsByCourse[ctx.dataIndex];
          return `${ctx.label}: ${formatInt(slice?.studentCount)} students (${formatPercent(slice?.percentage)})`;
        },
      },
    },
  },
});

const statusCfg = {
  PUBLISHED: { label: "Published", color: "#2e7d32", bg: "#e8f5e9", dot: "#4caf50" },
  DRAFT: { label: "Draft", color: "#8a6d00", bg: "#fff8e1", dot: "#f9a825" },
  ARCHIVED: { label: "Archived", color: "#555", bg: "#f0f0f0", dot: "#9e9e9e" },
};

function StatusCell({ value }) {
  const cfg = statusCfg[value] ?? {
    label: value ?? "—",
    color: "#555",
    bg: "#f5f5f5",
    dot: "#9e9e9e",
  };
  return (
    <Box
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: 0.6,
        px: 1.2,
        py: 0.4,
        borderRadius: "20px",
        background: cfg.bg,
      }}
    >
      <Box
        sx={{
          width: 7,
          height: 7,
          borderRadius: "50%",
          background: cfg.dot,
          flexShrink: 0,
        }}
      />
      <Typography sx={{ fontSize: "0.78rem", fontWeight: 700, color: cfg.color }}>
        {cfg.label}
      </Typography>
    </Box>
  );
}

function StatCard({ icon, iconBg, borderColor, glowColor, growth, caption, value, label }) {
  const hasGrowth = growth !== undefined && growth !== null;
  const down = hasGrowth && Number(growth) < 0;
  const tone = down ? { fg: "#c62828", bg: "#fdecea" } : { fg: "#2e7d32", bg: "#e8f5e9" };
  const TrendIcon = down ? TrendingDownIcon : TrendingUpIcon;
  return (
    <Card
      sx={{
        ...cardStyle,
        boxShadow: `0 2px 14px ${glowColor}, 0 1px 3px rgba(0,0,0,0.05)`,
        borderTop: `3px solid ${borderColor}`,
        transition: "transform 0.2s, box-shadow 0.2s",
        "&:hover": { transform: "translateY(-2px)" },
        height: "100%",
      }}
    >
      <CardContent sx={{ p: 1.75, "&:last-child": { pb: 1.75 } }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            mb: 1,
          }}
        >
          <Box
            sx={{
              background: iconBg,
              borderRadius: 1.5,
              width: 34,
              height: 34,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            {icon}
          </Box>
          <Typography
            variant="body2"
            color="text.secondary"
            fontWeight={600}
            sx={{ fontSize: "0.72rem" }}
          >
            {label}
          </Typography>
        </Box>

        <Typography sx={{ fontWeight: 800, fontSize: "1.3rem", color: "#1a1a2e" }}>
          {value}
        </Typography>

        {hasGrowth ? (
          <Box
            sx={{
              display: "inline-flex",
              alignItems: "center",
              gap: 0.3,
              mt: 0.75,
              px: 0.8,
              py: 0.2,
              borderRadius: "8px",
              background: tone.bg,
            }}
          >
            <TrendIcon sx={{ fontSize: 11, color: tone.fg }} />
            <Typography sx={{ fontWeight: 700, fontSize: "0.65rem", color: tone.fg }}>
              {growthLabel(growth)}
            </Typography>
          </Box>
        ) : (
          <Typography sx={{ mt: 0.75, fontWeight: 600, fontSize: "0.65rem", color: "text.secondary" }}>
            {caption}
          </Typography>
        )}
      </CardContent>
    </Card>
  );
}

function PieLegend({ studentsByCourse }) {
  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75, flex: 1, minWidth: 0 }}>
      {studentsByCourse.map((slice, i) => (
        <Box key={slice.label} sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
          <Box
            sx={{
              width: 7,
              height: 7,
              borderRadius: "50%",
              backgroundColor: pieColor(i),
              flexShrink: 0,
            }}
          />
          <Typography sx={{ fontSize: "0.72rem", color: "#555" }} noWrap>
            {slice.label}
          </Typography>
          <Typography
            sx={{ ml: "auto", fontWeight: 700, fontSize: "0.72rem", color: "#1a1a2e" }}
          >
            {formatPercent(slice.percentage)}
          </Typography>
        </Box>
      ))}
    </Box>
  );
}

function TopPerformingTestsCard({ topTests, page, rowsPerPage, onPageChange, onRowsPerPageChange, loading }) {
  const pagedTests = topTests?.content ?? [];
  const handleChangePage = (event, newPage) => onPageChange(newPage);
  const handleChangeRowsPerPage = (event) => onRowsPerPageChange(parseInt(event.target.value, 10));

  const columns = ["Test Name", "Exam", "Attempts", "Avg Score", "Status"];

  return (
    <>
      <Card sx={{ ...cardSx, mb: 2 }}>
        <CardContent sx={{ py: 1.5, "&:last-child": { pb: 1.5 } }}>
          <Typography sx={{ fontWeight: 700, fontSize: "1.05rem" }}>
            Top Performing Tests
          </Typography>
        </CardContent>
      </Card>

      <Paper sx={{ ...cardSx, width: "100%", overflow: "hidden" }}>
        <TableContainer sx={{ opacity: loading ? 0.6 : 1, transition: "opacity 0.2s" }}>
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                {columns.map((label) => (
                  <TableCell
                    key={label}
                    sx={{
                      padding: "10px 12px",
                      backgroundColor: ACCENT,
                      color: "white",
                      fontWeight: 600,
                      position: "sticky",
                      top: 0,
                      zIndex: 3,
                    }}
                  >
                    {label}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {pagedTests.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={columns.length} sx={{ textAlign: "center", py: 4 }}>
                    <AssessmentEmptyState message="No tests found" />
                  </TableCell>
                </TableRow>
              ) : (
                pagedTests.map((t) => (
                  <TableRow
                    key={t.testId}
                    hover
                    sx={{
                      "&:nth-of-type(odd)": {
                        backgroundColor: "rgba(0,0,0,0.02)",
                      },
                    }}
                  >
                    <TableCell sx={{ padding: "10px 12px" }}>
                      <Typography sx={{ fontWeight: 600, fontSize: "0.85rem" }}>
                        {t.testName}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ padding: "10px 12px" }}>{t.courseName ?? "\u2014"}</TableCell>
                    <TableCell sx={{ padding: "10px 12px" }}>{formatInt(t.attemptCount)}</TableCell>
                    <TableCell sx={{ padding: "10px 12px" }}>{formatPercent(t.avgScorePercentage)}</TableCell>
                    <TableCell sx={{ padding: "10px 12px" }}>
                      <StatusCell value={t.status} />
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          component="div"
          count={topTests?.totalElements ?? 0}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          sx={{
            borderTop: "1px solid",
            borderColor: "divider",
            "& .MuiTablePagination-toolbar": {
              paddingLeft: 2,
              paddingRight: 2,
            },
          }}
        />
      </Paper>
    </>
  );
}

const QUICK_ACTIONS = [
  { label: "New Test", icon: <AddCircleOutlineIcon />, path: "/assessment/tests/new", primary: true },
  { label: "Add Questions", icon: <QuizOutlinedIcon />, tab: "Questions" },
  { label: "Reports", icon: <AssessmentOutlinedIcon />, tab: "Reports" },
  { label: "Students", icon: <PeopleAltOutlinedIcon />, tab: "Students" },
];

const ASSESSMENT_DASHBOARD_FILTER_KEY = "assessmentDashboardDateFilter";

const DATE_FILTER_OPTIONS = [
  { label: "Today", value: "TODAY" },
  { label: "Yesterday", value: "YESTERDAY" },
  { label: "Last Week", value: "1W" },
  { label: "Last 1 Month", value: "1M" },
  { label: "Last 3 Months", value: "3M" },
  { label: "Last 6 Months", value: "6M" },
  { label: "Last 1 Year", value: "1Y" },
];

const dateFilterLabel = (activeFilter, startDate, endDate) => {
  if (activeFilter === "custom" && startDate && endDate)
    return `${startDate} – ${endDate}`;
  return (
    DATE_FILTER_OPTIONS.find((o) => o.value === activeFilter)?.label ??
    "Last 1 Month"
  );
};

const toIsoDate = (d) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

// Quick filters -> calendar-date window (endDate inclusive), computed from the
// browser's local "today". "1M"/"3M"/... are rolling windows ending today.
const resolveDateRange = (activeFilter, startDate, endDate) => {
  const today = new Date();
  const daysAgo = (n) => {
    const d = new Date(today);
    d.setDate(d.getDate() - n);
    return d;
  };
  const monthsAgo = (n) => {
    const d = new Date(today);
    d.setMonth(d.getMonth() - n);
    d.setDate(d.getDate() + 1);
    return d;
  };
  switch (activeFilter) {
    case "TODAY":
      return { startDate: toIsoDate(today), endDate: toIsoDate(today) };
    case "YESTERDAY":
      return { startDate: toIsoDate(daysAgo(1)), endDate: toIsoDate(daysAgo(1)) };
    case "1W":
      return { startDate: toIsoDate(daysAgo(6)), endDate: toIsoDate(today) };
    case "3M":
      return { startDate: toIsoDate(monthsAgo(3)), endDate: toIsoDate(today) };
    case "6M":
      return { startDate: toIsoDate(monthsAgo(6)), endDate: toIsoDate(today) };
    case "1Y":
      return { startDate: toIsoDate(monthsAgo(12)), endDate: toIsoDate(today) };
    case "custom":
      return { startDate, endDate };
    case "1M":
    default:
      return { startDate: toIsoDate(monthsAgo(1)), endDate: toIsoDate(today) };
  }
};

function AssessmentDashboard({ onNavigate }) {
  const navigate = useNavigate();
  const savedFilter = (() => {
    try {
      return (
        JSON.parse(localStorage.getItem(ASSESSMENT_DASHBOARD_FILTER_KEY)) || {}
      );
    } catch {
      return {};
    }
  })();

  const [courseFilter, setCourseFilter] = useState("");
  const [activeFilter, setActiveFilter] = useState(
    savedFilter.activeFilter || "1M",
  );
  const [anchorEl, setAnchorEl] = useState(null);
  const [startDate, setStartDate] = useState(savedFilter.startDate || "");
  const [endDate, setEndDate] = useState(savedFilter.endDate || "");

  React.useEffect(() => {
    localStorage.setItem(
      ASSESSMENT_DASHBOARD_FILTER_KEY,
      JSON.stringify({ activeFilter, startDate, endDate }),
    );
  }, [activeFilter, startDate, endDate]);

  const handleFilterChange = (val) => {
    setActiveFilter(val);
    setAnchorEl(null);
  };

  const handleCustomApply = () => {
    if (startDate && endDate) {
      setActiveFilter("custom");
      setAnchorEl(null);
    }
  };

  const filterLabel = dateFilterLabel(activeFilter, startDate, endDate);

  const [courses, setCourses] = useState([]);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [topPage, setTopPage] = useState(0);
  const [topSize, setTopSize] = useState(5);

  useEffect(() => {
    listCourses({ includeInactive: true })
      .then(setCourses)
      .catch((err) => toast.error(apiErrorMessage(err, "Failed to load exams")));
  }, []);

  // Back to the first top-tests page whenever the filters change.
  useEffect(() => {
    setTopPage(0);
  }, [courseFilter, activeFilter, startDate, endDate]);

  useEffect(() => {
    const range = resolveDateRange(activeFilter, startDate, endDate);
    let cancelled = false;
    setLoading(true);
    getAdminDashboard({
      courseId: courseFilter,
      startDate: range.startDate,
      endDate: range.endDate,
      topTestsPage: topPage,
      topTestsSize: topSize,
    })
      .then((res) => {
        if (!cancelled) setData(res);
      })
      .catch((err) => {
        if (!cancelled) toast.error(apiErrorMessage(err, "Failed to load dashboard"));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [courseFilter, activeFilter, startDate, endDate, topPage, topSize]);

  return (
    <Box sx={{ textAlign: "left" }}>
      <ToastContainer />
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 2 }}>
        {QUICK_ACTIONS.map((a) => (
          <Button
            key={a.label}
            size="small"
            variant={a.primary ? "contained" : "outlined"}
            startIcon={a.icon}
            onClick={() => (a.path ? navigate(a.path) : onNavigate?.(a.tab))}
            sx={{
              borderRadius: "999px",
              textTransform: "none",
              fontWeight: 700,
              px: 2,
              ...(a.primary ? { boxShadow: "none" } : { borderColor: SURFACE.border, color: ACCENT }),
            }}
          >
            {a.label}
          </Button>
        ))}
      </Box>

      <Box
        sx={{
          ...cardSx,
          display: "flex",
          flexWrap: "wrap",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 2,
          mb: 2.5,
          p: 1.5,
        }}
      >
        <FormControl size="small" sx={{ minWidth: 180 }}>
          <InputLabel>Exam</InputLabel>
          <Select
            label="Exam"
            value={courseFilter}
            onChange={(e) => setCourseFilter(e.target.value)}
          >
            <MenuItem value="">All Exams</MenuItem>
            {courses.map((c) => (
              <MenuItem key={c.id} value={c.id}>
                {c.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Date range filter — same pill + popover pattern as the Mentorship
            Payments/Analytics dashboards */}
        <Box>
          <Box
            onClick={(e) => setAnchorEl(e.currentTarget)}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              px: 2,
              py: 1,
              borderRadius: 2,
              cursor: "pointer",
              background: "rgba(255,255,255,0.85)",
              border: "1px solid #e8eaf0",
              boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
              transition: "all 0.2s",
              "&:hover": {
                boxShadow: "0 4px 16px rgba(0,0,0,0.1)",
                borderColor: "#bdbdbd",
              },
            }}
          >
            <CalendarTodayIcon sx={{ fontSize: 16, color: ACCENT }} />
            <Typography sx={{ fontSize: "0.82rem", fontWeight: 600, color: "#444" }}>
              {filterLabel}
            </Typography>
            <Box sx={{ fontSize: 10, color: "#aaa", ml: 0.5 }}>▼</Box>
          </Box>

          <Popover
            open={Boolean(anchorEl)}
            anchorEl={anchorEl}
            onClose={() => setAnchorEl(null)}
            anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
            transformOrigin={{ vertical: "top", horizontal: "right" }}
            PaperProps={{
              sx: {
                mt: 1,
                borderRadius: 3,
                boxShadow: "0 12px 40px rgba(0,0,0,0.13)",
                minWidth: 220,
                overflow: "hidden",
                border: "1px solid #f0f0f0",
              },
            }}
          >
            {DATE_FILTER_OPTIONS.map((opt) => (
              <Box
                key={opt.value}
                onClick={() => handleFilterChange(opt.value)}
                sx={{
                  px: 2.5,
                  py: 1.2,
                  cursor: "pointer",
                  fontSize: "0.83rem",
                  fontWeight: activeFilter === opt.value ? 700 : 500,
                  color: activeFilter === opt.value ? ACCENT : "#444",
                  background:
                    activeFilter === opt.value
                      ? "rgba(23,71,158,0.07)"
                      : "#fff",
                  borderLeft:
                    activeFilter === opt.value
                      ? `3px solid ${ACCENT}`
                      : "3px solid transparent",
                  transition: "all 0.15s",
                  "&:hover": {
                    background: "rgba(23,71,158,0.06)",
                    color: ACCENT,
                  },
                }}
              >
                {opt.label}
              </Box>
            ))}
            <Box sx={{ height: "1px", background: "#f0f0f0", mx: 2 }} />
            <Box sx={{ p: 2 }}>
              <Typography
                variant="caption"
                fontWeight={700}
                color="#aaa"
                sx={{ textTransform: "uppercase", letterSpacing: 1, fontSize: "0.68rem" }}
              >
                Custom Range
              </Typography>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1.2, mt: 1 }}>
                <TextField
                  label="Start Date"
                  type="date"
                  size="small"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                  sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2, fontSize: "0.82rem" } }}
                />
                <TextField
                  label="End Date"
                  type="date"
                  size="small"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  inputProps={{ min: startDate }}
                  fullWidth
                  sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2, fontSize: "0.82rem" } }}
                />
                <Box sx={{ display: "flex", gap: 1 }}>
                  <Button
                    size="small"
                    fullWidth
                    variant="outlined"
                    onClick={() => {
                      setStartDate("");
                      setEndDate("");
                      setActiveFilter("1M");
                      setAnchorEl(null);
                    }}
                    sx={{ borderRadius: 2, textTransform: "none", fontSize: "0.78rem" }}
                  >
                    Clear
                  </Button>
                  <Button
                    size="small"
                    fullWidth
                    variant="contained"
                    onClick={handleCustomApply}
                    disabled={!startDate || !endDate}
                    sx={{ borderRadius: 2, textTransform: "none", fontSize: "0.78rem" }}
                  >
                    Apply
                  </Button>
                </Box>
              </Box>
            </Box>
          </Popover>
        </Box>
      </Box>

      {!data ? (
        loading ? (
          <Box>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr", md: "repeat(4, 1fr)" },
                gap: 1.75,
                mb: 2.5,
              }}
            >
              {Array.from({ length: 8 }).map((_, i) => (
                <Skeleton key={i} variant="rounded" height={104} />
              ))}
            </Box>
            <Skeleton variant="rounded" height={320} />
          </Box>
        ) : (
          <Typography sx={{ py: 6, textAlign: "center", color: "text.secondary" }}>
            No dashboard data available
          </Typography>
        )
      ) : (
        <>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "1fr",
                sm: "1fr 1fr",
                md: "repeat(4, 1fr)",
              },
              gap: 1.75,
              mb: 2.5,
              opacity: loading ? 0.6 : 1,
              transition: "opacity 0.2s",
            }}
          >
            {statCards(data).map((card) => (
              <StatCard key={card.key} {...card} />
            ))}
          </Box>

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", md: "1.6fr 1fr" },
              gap: 2.5,
              mb: 2.5,
              opacity: loading ? 0.6 : 1,
              transition: "opacity 0.2s",
            }}
          >
            <Card sx={cardSx}>
              <CardContent sx={{ p: 3 }}>
                <Typography sx={{ fontWeight: 700, fontSize: "1.05rem", mb: 2 }}>
                  Test Attempts {"\u2014"} {filterLabel}
                </Typography>
                <Box sx={{ height: 300 }}>
                  {data.trend?.length ? (
                    <Line data={lineData(data.trend)} options={lineOptions(data.trend)} />
                  ) : (
                    <Typography sx={{ pt: 12, textAlign: "center", color: "text.secondary" }}>
                      No attempts in this period
                    </Typography>
                  )}
                </Box>
              </CardContent>
            </Card>

            <Card sx={cardSx}>
              <CardContent sx={{ p: 3 }}>
                <Typography sx={{ fontWeight: 700, fontSize: "1.05rem", mb: 2 }}>
                  Students by Exam
                </Typography>
                {data.studentsByCourse?.length ? (
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                    <Box sx={{ height: 280, width: 280, flexShrink: 0 }}>
                      <Pie
                        data={pieData(data.studentsByCourse)}
                        options={pieOptions(data.studentsByCourse)}
                      />
                    </Box>
                    <PieLegend studentsByCourse={data.studentsByCourse} />
                  </Box>
                ) : (
                  <Typography sx={{ py: 10, textAlign: "center", color: "text.secondary" }}>
                    No students yet
                  </Typography>
                )}
              </CardContent>
            </Card>
          </Box>

          <TopPerformingTestsCard
            topTests={data.topTests}
            page={topPage}
            rowsPerPage={topSize}
            loading={loading}
            onPageChange={setTopPage}
            onRowsPerPageChange={(size) => {
              setTopSize(size);
              setTopPage(0);
            }}
          />
        </>
      )}
    </Box>
  );
}

AssessmentDashboard.propTypes = { onNavigate: PropTypes.func };

export default AssessmentDashboard;
